import { ApolloClient } from '@apollo/client'
import { withFeatureFlags } from '@jusbrasil-web/shared-feature-flags'
import { parseUserCookie } from '@jusbrasil-web/shared-user-session'
import { GetServerSidePropsContext, NextApiRequest, NextApiResponse } from 'next'

import {
  COMPLETE_SIGNUP_NUDGE_DEFAULT_VALUE,
  COMPLETE_SIGNUP_NUDGE_FEATURE_KEY,
  CompleteSignupNudgeVariant,
} from './constants'
import { isCompleteSignupNudgeVariant } from './variant'

export interface CompleteSignupNudgeApiResponse {
  variant: CompleteSignupNudgeVariant | null
}

// Server-side eval — the GrowthBook proxy isn't reachable from the browser. The
// client only calls this once eligible, so the getFeatureValue read fires the
// exposure post-eligibility. apolloClient is injected: shared can't import scope:core.
export async function completeSignupNudgeApiHandler(
  req: NextApiRequest,
  res: NextApiResponse<CompleteSignupNudgeApiResponse>,
  { apolloClient }: { apolloClient: ApolloClient<unknown> }
): Promise<void> {
  // Never cache, on every path: the response is per-user but the URL is fixed,
  // so a cached entry would leak one user's variant to the next account on a
  // shared device — or serve a stale null after a logged-out user signs in.
  res.setHeader('Cache-Control', 'no-store')

  const session = parseUserCookie({ req })
  if (!session?.pid) {
    res.status(200).json({ variant: null })
    return
  }

  let variant: CompleteSignupNudgeVariant | null = null
  try {
    const ctx = { req, res } as unknown as GetServerSidePropsContext
    const { getFeatureValue } = await withFeatureFlags(ctx, apolloClient, {
      [COMPLETE_SIGNUP_NUDGE_FEATURE_KEY]: COMPLETE_SIGNUP_NUDGE_DEFAULT_VALUE,
    })
    const value = getFeatureValue(COMPLETE_SIGNUP_NUDGE_FEATURE_KEY)
    variant = isCompleteSignupNudgeVariant(value) ? value : null
  } catch {
    // Fail closed — a GrowthBook hiccup serves no nudge instead of a 500.
    variant = null
  }

  res.status(200).json({ variant })
}
